import type { FieldSchema, LinkValue, SectionSchema, SubFormSchema } from './types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { RichText } from '@sunwebgroup/uds-ui/rich-text'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useWatch } from 'react-hook-form'
import { useIntl } from '@/intl'
import { CheckboxGroupInput } from '@/scopes/common/components/FormElements/CheckboxGroupInput'
import { CheckboxInput } from '@/scopes/common/components/FormElements/CheckboxInput'
import { DropdownInput } from '@/scopes/common/components/FormElements/DropdownInput'
import { RadioButtonInput } from '@/scopes/common/components/FormElements/RadioButtonInput'
import { TextInput } from '@/scopes/common/components/FormElements/TextInput'
import { ToggleButtonGroupInput } from '@/scopes/common/components/FormElements/ToggleButtonGroupInput'
import styles from './SchemaRenderer.module.css'

interface SchemaRendererProps<TValues extends Record<string, unknown>> {
  schema: SubFormSchema<TValues>
}

function buildLinkValues(
  links: Record<string, LinkValue> | undefined,
  formatMessage: (descriptor: { id: string }) => string,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(links ?? {}).map(([placeholder, linkValue]) => {
      const label = 'label' in linkValue ? linkValue.label : formatMessage({ id: linkValue.labelKey })
      return [placeholder, `<a href="${linkValue.href}" target="_blank" rel="noopener noreferrer">${label}</a>`]
    }),
  )
}

function resolveBoolean<TValues>(
  rule: boolean | ((values: TValues) => boolean) | undefined,
  values: TValues,
  defaultValue: boolean,
): boolean {
  if (rule === undefined)
    return defaultValue
  if (typeof rule === 'boolean')
    return rule
  return rule(values)
}

function FieldRenderer<TValues extends Record<string, unknown>>({
  field,
  values,
}: {
  field: FieldSchema<TValues>
  values: TValues
}) {
  const intl = useIntl()

  if (field.kind === 'banner') {
    const isVisible = resolveBoolean(field.visibleWhen, values, true)
    if (!isVisible)
      return null

    const message = intl.formatMessageIfExists({ id: field.messageKey })
    if (!message)
      return null

    const linkValues = buildLinkValues(field.links, intl.formatMessage)

    return (
      <Banner hasIcon color="informative" size="M">
        <Banner.TextsWrapper className={styles.bannerTexts}>
          <Banner.SupportingText innerHTML={intl.formatMessage({ id: field.messageKey }, linkValues)} />
        </Banner.TextsWrapper>
      </Banner>
    )
  }

  if (field.kind === 'paragraph') {
    const isVisible = resolveBoolean(field.visibleWhen, values, true)
    if (!isVisible)
      return null

    const message = intl.formatMessageIfExists({ id: field.messageKey })
    if (!message)
      return null

    return <Typography as="p" variant="body-m-regular-loose">{message}</Typography>
  }

  const isVisible = resolveBoolean(field.visibleWhen, values, true)
  const isRequired = resolveBoolean(field.required, values, false)

  if (!isVisible)
    return null

  const label = intl.formatMessage({ id: field.labelKey })
  const requiredErrorMessage = field.requiredErrorKey
    ? intl.formatMessage({ id: field.requiredErrorKey })
    : undefined

  if (field.kind === 'text') {
    return (
      <TextInput
        name={field.name}
        label={label}
        required={isRequired}
        requiredErrorMessage={requiredErrorMessage}
        pattern={field.pattern}
        patternErrorMessage={field.patternErrorKey ? intl.formatMessage({ id: field.patternErrorKey }) : undefined}
        helpText={field.helpTextKey ? intl.formatMessage({ id: field.helpTextKey }) : undefined}
        formFieldClass={styles.textInputClass}
      />
    )
  }

  if (field.kind === 'dropdown') {
    return (
      <DropdownInput
        name={field.name}
        label={label}
        required={isRequired}
        requiredErrorMessage={requiredErrorMessage}
        items={field.options.map(opt => ({
          value: opt.value,
          text: intl.formatMessage({ id: opt.labelKey }),
        }))}
        placeholder={field.placeholderKey ? intl.formatMessage({ id: field.placeholderKey }) : undefined}
      />
    )
  }

  if (field.kind === 'toggle') {
    return (
      <ToggleButtonGroupInput
        name={field.name}
        label={label}
        required={isRequired}
        requiredErrorMessage={requiredErrorMessage}
        items={field.options.map(opt => ({
          value: opt.value,
          title: intl.formatMessage({ id: opt.labelKey }),
        }))}
      />
    )
  }

  if (field.kind === 'checkbox') {
    const linkValues = buildLinkValues(field.links, intl.formatMessage)
    const hasLinks = Object.keys(field.links ?? {}).length > 0
    const checkboxLabel = hasLinks
      ? <RichText tag="span" innerHTML={intl.formatMessage({ id: field.labelKey }, linkValues)} />
      : label

    return (
      <CheckboxInput
        name={field.name}
        label={checkboxLabel}
        validationMessage={field.validationMessageKey ? intl.formatMessage({ id: field.validationMessageKey }) : undefined}
      />
    )
  }

  if (field.kind === 'checkboxGroup') {
    return (
      <div className={styles.checkboxGroupWrapper}>
        <Typography>{intl.formatMessage({ id: field.labelKey })}</Typography>
        <div className={styles.checkboxGroup}>
          {field.options.map(opt => (
            <CheckboxGroupInput
              name={field.name}
              value={opt.value}
              label={intl.formatMessage({ id: opt.labelKey })}
              key={opt.value}
            />
          ),
          )}
        </div>
      </div>
    )
  }

  if (field.kind === 'choiceList') {
    return (
      <RadioButtonInput
        name={field.name}
        label={label}
        required={isRequired}
        requiredErrorMessage={requiredErrorMessage}
        items={field.options.map(opt => ({
          value: opt.value,
          label: intl.formatMessage({ id: opt.labelKey }),
        }))}
      />
    )
  }

  return null
}

function SectionRenderer<TValues extends Record<string, unknown>>({
  section,
  values,
}: {
  section: SectionSchema<TValues>
  values: TValues
}) {
  const intl = useIntl()

  const isVisible = resolveBoolean(section.visibleWhen, values, true)
  if (!isVisible)
    return null

  const activeTitleKey = section.conditionalTitles?.find(ct => ct.visibleWhen(values))?.titleKey ?? section.titleKey

  return (
    <div className={styles.section}>
      {activeTitleKey && (
        <Typography as="h3" variant="display-h-regular" className={styles.sectionTitle}>{intl.formatMessage({ id: activeTitleKey })}</Typography>
      )}
      <div className={section.layout === 'row' ? styles.fieldsRow : styles.fieldsStack}>
        {section.fields.map(field => (
          <FieldRenderer key={field.kind === 'banner' || field.kind === 'paragraph' ? field.messageKey : field.name} field={field} values={values} />
        ))}
      </div>
    </div>
  )
}

export function SchemaRenderer<TValues extends Record<string, unknown>>({
  schema,
}: SchemaRendererProps<TValues>) {
  const values = useWatch() as TValues

  return (
    <>
      {schema.sections.map(section => (
        <SectionRenderer key={section.id} section={section} values={values} />
      ))}
    </>
  )
}
